import { Link } from 'react-router-dom';
import PageHeader from '../components/PageHeader';
import { useApi } from '../lib/api';
import { formatBytes, formatRelative } from '../lib/format';

type Row = {
  id: string;
  recipient_name: string;
  status: string;
  created_at: string;
  guest_count: number;
  upload_count: number;
  db_bytes: string;
};

type Resp = { rows: Row[]; total: number };

export default function Storage() {
  const { data, error, loading } = useApi<Resp>('/api/experiences?limit=200');

  const sorted = data
    ? [...data.rows].sort((a, b) => Number(b.db_bytes) - Number(a.db_bytes))
    : [];

  const totalBytes = sorted.reduce((sum, r) => sum + Number(r.db_bytes), 0);

  return (
    <div>
      <PageHeader
        title="Storage"
        subtitle="Per-experience size from uploads.file_size_bytes. Click an experience to scan R2 (includes avatars + orphans)."
      />

      {loading && <p className="text-sm text-slate-500">Loading…</p>}
      {error && <p className="text-sm text-red-600">Error: {error.message}</p>}

      {data && (
        <>
          <div className="mb-4 rounded-md border border-slate-200 bg-white p-4">
            <div className="text-xs uppercase tracking-wide text-slate-500">Sum of DB bytes (top {sorted.length} experiences)</div>
            <div className="mt-1 text-2xl font-semibold">{formatBytes(totalBytes)}</div>
          </div>

          <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white">
            <table className="min-w-full text-sm">
              <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="px-4 py-2 font-medium">Recipient</th>
                  <th className="px-4 py-2 font-medium text-right">Uploads</th>
                  <th className="px-4 py-2 font-medium text-right">Guests</th>
                  <th className="px-4 py-2 font-medium text-right">Size (DB)</th>
                  <th className="px-4 py-2 font-medium">Created</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {sorted.map((row) => (
                  <tr key={row.id} className="hover:bg-slate-50">
                    <td className="px-4 py-2">
                      <Link to={`/experiences/${row.id}`} className="text-blue-700 hover:underline">
                        {row.recipient_name}
                      </Link>
                    </td>
                    <td className="px-4 py-2 text-right tabular-nums">{row.upload_count}</td>
                    <td className="px-4 py-2 text-right tabular-nums">{row.guest_count}</td>
                    <td className="px-4 py-2 text-right tabular-nums font-medium">{formatBytes(Number(row.db_bytes))}</td>
                    <td className="px-4 py-2 text-slate-600" title={row.created_at}>{formatRelative(row.created_at)}</td>
                  </tr>
                ))}
                {sorted.length === 0 && (
                  <tr><td colSpan={5} className="px-4 py-6 text-center text-slate-500">No experiences with storage.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
